import { GetServerSideProps, NextPage } from 'next';

import { EventLectureDetail } from '@/containers/Event/EventLectureDetail';
import { queryThinLectureWithTag } from '@/fetchers/thinLectures';
import { useQueryParams } from '@/hooks/useQueryParams';
import { queryThinEventSession } from '@/queries/thin-event-sessions';
import { queryThinEvent } from '@/queries/thin-events';
import { queryTranscriptsByLectureId } from '@/queries/transcripts';
import { generateStore } from '@/utils/generateStore';
import { splitIdAndSlug } from '@/utils/helperFunctions';
import { Hydrate, IHydrateProps } from '@/utils/swr/Hydrate';
import { StringParam, urlQueryStringValidate } from '@/utils/url-query-string';

export interface IUrlQueryStringParams {
  eventId?: string;
  sessionId?: string;
  lectureId?: string;
}

const LecturePage: NextPage<IHydrateProps> = ({ fallback }) => {
  const { eventId, sessionId, lectureId: lectureSlug } = useQueryParams<IUrlQueryStringParams>({
    eventId: StringParam,
    lectureId: StringParam,
    sessionId: StringParam,
  });

  const { id: lectureId } = splitIdAndSlug(lectureSlug);

  return (
    <Hydrate fallback={fallback}>
      <EventLectureDetail eventId={eventId} sessionId={sessionId} lectureId={lectureId} />
    </Hydrate>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const store = generateStore({ context });

  const { eventId, sessionId, lectureId: lectureSlug } = urlQueryStringValidate<IUrlQueryStringParams>(context.query, {
    eventId: StringParam,
    sessionId: StringParam,
    lectureId: StringParam,
  });

  const { id: lectureId } = splitIdAndSlug(lectureSlug);

  try {
    await Promise.all([
      store.fetchResource(queryThinEvent(eventId)),
      store.fetchResources(queryTranscriptsByLectureId(lectureId)),
      store.fetchResource(queryThinEventSession(sessionId)),
      store.fetchResource(queryThinLectureWithTag(lectureId)),
    ]);

    return {
      props: {
        fallback: store.fallback,
      },
    };
  } catch (err) {
    return {
      props: {
        fallback: store.fallback,
      },
    };
  }
};

export default LecturePage;
